<script setup>
import { computed, ref } from "vue";

const props = defineProps({
    labelText: {
        type: String,
        default: "",
    },
    labelClass: {
        type: String,
        default: "",
    },
    subLabelText: {
        type: String,
        default: "",
    },
    placeholder: {
        type: String,
        default: "",
    },
    inputClass: {
        type: String,
        default: "",
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    type: {
        type: String,
        default: "text",
    },
    optional: {
        type: Boolean,
        default: false,
    },
    required: {
        type: Boolean,
        default: false,
    },
    modelValue: {
        type: String,
        default: "",
    },
    value: {
        type: String,
        default: "",
    },
    error: {
        type: String,
        default: "",
    },
    testId: {
        type: String,
        default: "",
    },
    dataTest: {
        type: String,
        default: "",
    },
    maxlength: {
        type: Number,
        default: 45,
    },
    isFullWidth: {
        type: Boolean,
        default: false,
    },
    min: {
        type: Number,
    },
    max: {
        type: Number,
    },
    currency: {
        type: String,
        default: "£",
    },
});

const emit = defineEmits(["update:modelValue"]);
const showPassword = ref(false);

const toggleShowPassword = () => {
    showPassword.value = !showPassword.value;
};

const updateValue = (event) => {
    emit("update:modelValue", event.target.value);
};

const inputType = computed(() => {
    if (showPassword.value) { return "text"; }

    if (props.type === "price") { return "number"; }

    return props.type;
});

const isNumber = computed(() => props.type === "number" || props.type === "price");

</script>

<template>
    <label :class="props?.labelClass" v-if="props?.labelText">
        {{ props?.labelText }}
        {{ optional ? "(optional)" : "" }}
        <sup v-if="props?.required" class="required-field">*</sup>
    </label>
    <div v-if="props?.subLabelText" class="sub-label">
        {{ subLabelText }}
    </div>
    <div class="position-relative d-flex flex-column">
        <div class="priceSign" v-if="type === 'price'">
            {{ props.currency }}
        </div>
        <input
            :class="{
                [props?.inputClass]: props?.inputClass,
                'error-input': props.error !== '',
                'w-100': props.isFullWidth,
                'pricingInput': type === 'price'
            }"
            :type="inputType"
            :value="value"
            :placeholder="props?.placeholder"
            @input="updateValue"
            :disabled="props.disabled"
            :maxlength="maxlength"
            :data-test-id="testId"
            :data-test="dataTest"
            :data-type="type"
            :min="isNumber ? min : null"
            :max="isNumber ? max : null"
        />
        <i
            v-if="type === 'password'"
            @click="toggleShowPassword"
            class="iconTogglePassword fas"
            :class="{ 'fa-eye-slash': showPassword, 'fa-eye': !showPassword }"
        ></i>
    </div>

    <div class="error-block" data-test="validation-error">
        {{ props.error }}
    </div>
</template>

<style scoped>
.min-w-300{
    min-width: 300px;
}

.w-300 {
    width: 300px;
}

input:focus-visible {
    outline-offset: 0px;
    outline: 0px;
}

:focus-visible {
    outline: -webkit-focus-ring-color auto 1px;
    outline-color: -webkit-focus-ring-color;
    outline-style: auto;
    outline-width: 1px;
}

input {
    background: #ffffff;
    border: 1px solid #dddddd;
    box-sizing: border-box;
    border-radius: 10px;
    padding: 12px;
}

.iconTogglePassword {
    display: block;
    min-width: 20px;
    text-align: center;
    cursor: pointer;
    position: absolute;
    top: 43px;
    right: 15px;
    color: #808080;
}

input.error-input {
    border: 1px solid #ed5a5e;
}

label {
    font-weight: 600;
    visibility: visible !important;
    opacity: 1 !important;
    font-size: 12px;
}

.sub-label {
    font-style: italic;
    font-weight: 400;
    font-size: 0.881rem;
    line-height: 1.381rem;
    color: rgba(153, 153, 153, 1);
    margin-bottom: 8px;
}

.required-field {
    color: #ed5a5e;
    margin-left: -2px;
}

.error-block {
    margin-top: 3px;
    color: #eb4247;
    font-weight: 400;
    font-size: 0.881rem;
    line-height: 1.381rem;
}

input:disabled {
    background: #eeeeee;
    border: 1px solid #cccccc;
    border-radius: 10px;
    color: #999999;
}

.priceSign{
    position: absolute;
    top: 12.5px;
    left: 16px;
}

.pricingInput{
    padding-left: 28px;
}
</style>
